"use client";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useState } from "react";
import { MdFilterNone } from "react-icons/md";
import { ContractTypeHelpDialogContent } from "./ContractTypeHelpDialogContent";

// フィルタータイプの定義
export type FacilityFilterType =
  | "multiLanguage" // 外国語対応
  | "barrierFree" // バリアフリー対応
  | "femaleOnly" // 女性専用
  | "maleOnly" // 男性専用
  | "directContract" // 直契約
  | "toshinkyoContract"; // 東振協契約

// フィルタ項目の定義
type FacilityFilter = {
  id: FacilityFilterType;
  label: string;
  group: "facility" | "segment" | "contract"; // facility: 施設機能, segment: 施設タイプ, contract: 契約形態
};

// フィルタリストの定義
const FACILITY_FILTERS: FacilityFilter[] = [
  { id: "multiLanguage", label: "外国語対応", group: "facility" },
  { id: "barrierFree", label: "バリアフリー対応", group: "facility" },
];

const SEGMENT_FILTERS: FacilityFilter[] = [
  { id: "femaleOnly", label: "女性専用", group: "segment" },
  { id: "maleOnly", label: "男性専用", group: "segment" },
];

const CONTRACT_FILTERS: FacilityFilter[] = [
  { id: "directContract", label: "直契約", group: "contract" },
  { id: "toshinkyoContract", label: "東振協契約", group: "contract" },
];

export const FacilityFilters = ({
  onChange,
  sex,
  fiscalYear,
}: {
  onChange?: (filters: Record<FacilityFilterType, boolean>) => void;
  sex?: "male" | "female" | null;
  fiscalYear: number;
}) => {
  const [selectedFilters, setSelectedFilters] = useState<
    Record<FacilityFilterType, boolean>
  >({
    multiLanguage: false,
    barrierFree: false,
    femaleOnly: false,
    maleOnly: false,
    directContract: false,
    toshinkyoContract: false,
  });

  // フィルタの選択状態を切り替える
  const toggleFilter = (filterId: FacilityFilterType) => {
    let newFilters = { ...selectedFilters };

    // 直契約と東振協契約は排他的
    if (filterId === "directContract" && !selectedFilters.directContract) {
      newFilters = {
        ...newFilters,
        directContract: true,
        toshinkyoContract: false,
      };
    } else if (
      filterId === "toshinkyoContract" &&
      !selectedFilters.toshinkyoContract
    ) {
      newFilters = {
        ...newFilters,
        toshinkyoContract: true,
        directContract: false,
      };
    } else {
      newFilters = { ...newFilters, [filterId]: !selectedFilters[filterId] };
    }

    setSelectedFilters(newFilters);

    // 親コンポーネントに変更を通知
    if (onChange) {
      onChange(newFilters);
    }
  };

  // フィルタチェックボックスコンポーネント
  const FilterCheckbox = ({ filter }: { filter: FacilityFilter }) => (
    <div
      className="flex cursor-pointer items-center gap-2"
      onClick={() => toggleFilter(filter.id)}
    >
      <Checkbox
        checked={selectedFilters[filter.id]}
        onCheckedChange={() => toggleFilter(filter.id)}
        className="data-[state=checked]:border-primary-6 data-[state=checked]:bg-primary-6"
      />
      <span className="text-sm">{filter.label}</span>
    </div>
  );

  // 性別に基づいて表示する施設タイプフィルターを決定
  const getSegmentFiltersToShow = () => {
    if (sex === "male") {
      // 男性の場合は男性専用フィルターのみ表示
      return SEGMENT_FILTERS.filter((filter) => filter.id === "maleOnly");
    } else if (sex === "female") {
      // 女性の場合は女性専用フィルターのみ表示
      return SEGMENT_FILTERS.filter((filter) => filter.id === "femaleOnly");
    }
    // 性別が不明な場合は表示しない
    return [];
  };

  return (
    <div className="flex flex-col gap-6 sm:flex-row sm:gap-12">
      <div className="w-full sm:w-auto">
        <h2 className="mb-4 text-sm font-bold">施設機能</h2>
        <div className="flex flex-row flex-wrap gap-x-6 gap-y-3">
          {FACILITY_FILTERS.map((filter) => (
            <FilterCheckbox key={filter.id} filter={filter} />
          ))}
        </div>
      </div>

      {sex !== null && (
        <div className="mt-2 w-full sm:mt-0 sm:w-auto">
          <h2 className="mb-4 text-sm font-bold">施設タイプ</h2>
          <div className="flex flex-row flex-wrap gap-x-6 gap-y-3">
            {getSegmentFiltersToShow().map((filter) => (
              <FilterCheckbox key={filter.id} filter={filter} />
            ))}
          </div>
        </div>
      )}

      <div className="mt-2 w-full sm:mt-0 sm:w-auto">
        <div className="mb-4 flex items-baseline">
          <h2 className="text-sm font-bold">契約形態</h2>
          <Dialog>
            <DialogTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center text-xs text-primary-6 hover:text-primary-7"
              >
                （契約形態について
                <MdFilterNone className="h-3 w-3" aria-hidden="true" />）
              </button>
            </DialogTrigger>
            <DialogContent className="mx-auto max-w-[calc(100%-3rem)] p-6 md:max-w-xl">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold">
                  契約形態について
                </DialogTitle>
                <DialogDescription>
                  当組合が契約している健診機関には、「直契約健診機関」と「東振協契約健診機関」の2種類があります。
                </DialogDescription>
              </DialogHeader>
              <ContractTypeHelpDialogContent fiscalYear={fiscalYear} />
            </DialogContent>
          </Dialog>
        </div>
        <div className="flex flex-row flex-wrap gap-x-6 gap-y-3">
          {CONTRACT_FILTERS.map((filter) => (
            <FilterCheckbox key={filter.id} filter={filter} />
          ))}
        </div>
      </div>
    </div>
  );
};
